import { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { cmsService } from '@services/api'
import {
  Ticket,
  Search,
  Eye,
  RefreshCcw,
  Download,
  MapPin,
  Clock,
  CreditCard,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RotateCcw,
  User,
  Phone,
  Calendar,
  Filter,
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  SimpleGrid,
  Box,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  useToast,
  Tooltip,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tag,
  TagLabel,
  TagLeftIcon,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface TicketData {
  id: string
  bookingReference: string
  status: string
  totalFare: number
  seatCount: number
  seatNumbers?: string[]
  boardedAt?: string | null
  createdAt: string
  updatedAt: string
  passenger?: {
    id: string
    name: string
    phone: string
    email: string
    isGuest?: boolean
  }
  route?: {
    id: string
    name: string
    startPoint: string
    endPoint: string
    code: string
  }
  trip?: {
    id: string
    status: string
    scheduledDeparture?: string | null
    scheduledArrival?: string | null
    actualDeparture?: string | null
    actualArrival?: string | null
  }
  payment?: {
    id: string
    amount: number
    status: string
    method?: string
  }
  boardingStop?: { name: string; stopCode: string } | null
  alightingStop?: { name: string; stopCode: string } | null
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isUnused = (t: TicketData) =>
  (t.status === 'confirmed' || t.status === 'pending') && !t.boardedAt

// A ticket can only be refunded if it was paid AND unused
const canRefund = (t: TicketData) =>
  isUnused(t) && t.payment?.status === 'paid'

const fmtDate = (iso?: string | null) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

const fmtAmt = (n: number) => `TZS ${Number(n).toLocaleString()}`

// ─── Status helpers ───────────────────────────────────────────────────────────

const bookingStatusColor = (s: string) => {
  switch (s) {
    case 'confirmed': return 'green'
    case 'pending': return 'yellow'
    case 'cancelled': return 'red'
    case 'completed': return 'blue'
    case 'no_show': return 'gray'
    default: return 'gray'
  }
}

const paymentStatusColor = (s?: string) => {
  switch (s) {
    case 'paid': return 'green'
    case 'refunded': return 'purple'
    case 'pending': return 'yellow'
    case 'failed': return 'red'
    default: return 'gray'
  }
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

function TicketDetailModal({
  isOpen,
  onClose,
  ticket,
  onRefund,
}: {
  isOpen: boolean
  onClose: () => void
  ticket: TicketData | null
  onRefund: (t: TicketData) => void
}) {
  if (!ticket) return null
  const unused = isUnused(ticket)
  const refundable = canRefund(ticket)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.600" />
      <ModalContent borderRadius="2xl" m={4}>
        <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={4}>
          <HStack spacing={3}>
            <Box p={2} bg="purple.100" borderRadius="lg">
              <Ticket size={20} color="var(--chakra-colors-purple-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold">Ticket Details</Text>
              <Text fontSize="sm" color="gray.500" fontWeight="normal">
                Ref: {ticket.bookingReference}
              </Text>
            </VStack>
            {unused && (
              <Badge colorScheme={refundable ? 'orange' : 'gray'} borderRadius="full" px={3} py={1} ml="auto" fontSize="xs">
                {refundable ? '🎫 Unused — Can Refund' : '🎫 Unused — Not Paid'}
              </Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={5}>
          <VStack spacing={5} align="stretch">

            {/* Unused Alert */}
            {unused && (
              <Alert status={refundable ? 'warning' : 'info'} borderRadius="xl">
                <AlertIcon />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" fontWeight="semibold">This ticket was never used</Text>
                  <Text fontSize="xs" color="gray.600">
                    {refundable
                      ? `The commuter booked and paid but never boarded. You can void this ticket and return ${fmtAmt(ticket.totalFare)} to their wallet.`
                      : 'The commuter booked but the ticket was not paid, so no refund is applicable.'
                    }
                  </Text>
                </VStack>
              </Alert>
            )}

            {/* Commuter info */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody py={3}>
                  <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={2}>Commuter</Text>
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <User size={14} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="semibold">{ticket.passenger?.name ?? '—'}</Text>
                      {ticket.passenger?.isGuest && (
                        <Badge colorScheme="purple" fontSize="0.6em" borderRadius="full">Guest</Badge>
                      )}
                    </HStack>
                    <HStack>
                      <Phone size={12} color="var(--chakra-colors-gray-400)" />
                      <Text fontSize="xs" color="gray.600">{ticket.passenger?.phone ?? '—'}</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">{ticket.passenger?.email}</Text>
                  </VStack>
                </CardBody>
              </Card>

              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody py={3}>
                  <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={2}>Ticket Status</Text>
                  <VStack align="start" spacing={2}>
                    <HStack>
                      <Text fontSize="xs" color="gray.500">Booking:</Text>
                      <Badge colorScheme={bookingStatusColor(ticket.status)} borderRadius="full" px={2} textTransform="capitalize">
                        {ticket.status}
                      </Badge>
                    </HStack>
                    <HStack>
                      <Text fontSize="xs" color="gray.500">Payment:</Text>
                      <Badge colorScheme={paymentStatusColor(ticket.payment?.status)} borderRadius="full" px={2} textTransform="capitalize">
                        {ticket.payment?.status ?? 'N/A'}
                      </Badge>
                    </HStack>
                    <HStack>
                      <Text fontSize="xs" color="gray.500">Boarded:</Text>
                      <Badge colorScheme={ticket.boardedAt ? 'green' : 'red'} borderRadius="full" px={2}>
                        {ticket.boardedAt ? `Yes — ${fmtDate(ticket.boardedAt)}` : 'No'}
                      </Badge>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Route + fare */}
            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody py={3}>
                <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={2}>Journey</Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <MapPin size={12} color="var(--chakra-colors-purple-500)" />
                      <Text fontSize="xs" color="gray.500">Route</Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="semibold">{ticket.route?.name ?? '—'}</Text>
                    <Text fontSize="xs" color="gray.500">
                      {ticket.route?.startPoint} → {ticket.route?.endPoint}
                    </Text>
                  </VStack>
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <Clock size={12} color="var(--chakra-colors-blue-500)" />
                      <Text fontSize="xs" color="gray.500">Scheduled Departure</Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="semibold">{fmtDate(ticket.trip?.scheduledDeparture)}</Text>
                  </VStack>
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <CreditCard size={12} color="var(--chakra-colors-green-500)" />
                      <Text fontSize="xs" color="gray.500">Fare Paid</Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="bold" color="green.600">{fmtAmt(ticket.totalFare)}</Text>
                    <Text fontSize="xs" color="gray.500">{ticket.seatCount} seat{ticket.seatCount !== 1 ? 's' : ''}</Text>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* Stops */}
            {(ticket.boardingStop || ticket.alightingStop) && (
              <SimpleGrid columns={2} spacing={4}>
                <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                  <CardBody py={3}>
                    <Text fontSize="xs" color="gray.500" mb={1}>Boarding Stop</Text>
                    <Text fontSize="sm" fontWeight="semibold">{ticket.boardingStop?.name ?? '—'}</Text>
                    <Text fontSize="xs" color="gray.400">[{ticket.boardingStop?.stopCode}]</Text>
                  </CardBody>
                </Card>
                <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                  <CardBody py={3}>
                    <Text fontSize="xs" color="gray.500" mb={1}>Drop-off Stop</Text>
                    <Text fontSize="sm" fontWeight="semibold">{ticket.alightingStop?.name ?? '—'}</Text>
                    <Text fontSize="xs" color="gray.400">[{ticket.alightingStop?.stopCode}]</Text>
                  </CardBody>
                </Card>
              </SimpleGrid>
            )}

            <Divider />

            <SimpleGrid columns={2} spacing={4}>
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="gray.500">Booked on</Text>
                <Text fontSize="sm" fontWeight="semibold">{fmtDate(ticket.createdAt)}</Text>
              </VStack>
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="gray.500">Last updated</Text>
                <Text fontSize="sm" fontWeight="semibold">{fmtDate(ticket.updatedAt)}</Text>
              </VStack>
            </SimpleGrid>

          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
          <Button variant="outline" onClick={onClose} flex={1} borderRadius="xl">Close</Button>
          {refundable && (
            <Button
              colorScheme="orange"
              flex={1}
              borderRadius="xl"
              leftIcon={<RefreshCcw size={16} />}
              onClick={() => { onClose(); onRefund(ticket) }}
            >
              Void & Refund {fmtAmt(ticket.totalFare)}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// ─── Refund Modal ─────────────────────────────────────────────────────────────

function RefundModal({
  isOpen,
  onClose,
  ticket,
}: {
  isOpen: boolean
  onClose: () => void
  ticket: TicketData | null
}) {
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  const handleConfirm = async () => {
    if (!ticket) return
    setLoading(true)
    try {
      await cmsService.processRefund(ticket.id, {
        reason: reason || 'Voided by admin',
        refundAmount: ticket.totalFare || undefined,
      })
      toast({
        title: '✅ Ticket Voided & Refunded',
        description: `${fmtAmt(ticket.totalFare)} has been credited to ${ticket.passenger?.name ?? 'the commuter'}'s wallet.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      queryClient.invalidateQueries('tickets')
      onClose()
    } catch (err: any) {
      toast({
        title: 'Refund Failed',
        description:
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Could not process the refund. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  if (!ticket) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" onCloseComplete={() => setReason('')}>
      <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.700" />
      <ModalContent borderRadius="2xl" m={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="orange.100" borderRadius="lg">
              <RefreshCcw size={20} color="var(--chakra-colors-orange-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold">Void Ticket & Refund</Text>
              <Text fontSize="sm" color="gray.500" fontWeight="normal">{ticket.bookingReference}</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={3}>
          <VStack spacing={5} align="stretch">
            <Alert status="warning" borderRadius="xl" variant="left-accent">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                This will <strong>cancel the booking</strong> and return money to the commuter's in-app wallet.
                The commuter can then use the wallet balance for future bookings.
              </AlertDescription>
            </Alert>

            {/* Summary card */}
            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody py={3}>
                <SimpleGrid columns={2} spacing={3}>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500">Commuter</Text>
                    <Text fontSize="sm" fontWeight="semibold">{ticket.passenger?.name ?? '—'}</Text>
                    <Text fontSize="xs" color="gray.500">{ticket.passenger?.phone}</Text>
                  </VStack>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500">Route</Text>
                    <Text fontSize="sm" fontWeight="semibold">{ticket.route?.name ?? '—'}</Text>
                    <Text fontSize="xs" color="gray.500">{ticket.route?.startPoint} → {ticket.route?.endPoint}</Text>
                  </VStack>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500">Ticket Status</Text>
                    <Badge colorScheme={bookingStatusColor(ticket.status)} borderRadius="full" px={2} textTransform="capitalize">
                      {ticket.status}
                    </Badge>
                  </VStack>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500">Boarded?</Text>
                    <Badge colorScheme="red" borderRadius="full" px={2}>
                      No — Unused
                    </Badge>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            <Divider />

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Refund Amount
              </FormLabel>
              <Input
                value={fmtAmt(ticket.totalFare)}
                isReadOnly
                bg="gray.100"
                borderRadius="xl"
                fontWeight="bold"
                fontSize="lg"
                cursor="not-allowed"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Amount is locked to the original ticket fare paid.
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">Reason (shown in audit log)</FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this ticket being voided?"
                rows={3}
                borderRadius="xl"
                resize="none"
                onClick={() => !reason && setReason('Ticket voided by admin — commuter did not board')}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3} pt={3} borderTop="1px solid" borderColor="gray.100">
          <Button variant="outline" onClick={onClose} flex={1} borderRadius="xl" isDisabled={loading}>
            Cancel
          </Button>
          <Button
            colorScheme="orange"
            flex={2}
            borderRadius="xl"
            leftIcon={<RefreshCcw size={16} />}
            isLoading={loading}
            loadingText="Processing refund..."
            onClick={handleConfirm}
          >
            Confirm — Return {fmtAmt(ticket.totalFare)} to Wallet
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TicketsPage() {
  const toast = useToast()

  // Filters
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterPayment, setFilterPayment] = useState('all')
  const [filterUsage, setFilterUsage] = useState('all') // all | unused | boarded
  const [filterRoute, setFilterRoute] = useState('all')
  const [page, setPage] = useState(1)
  const PER_PAGE = 15

  // Modals
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure()
  const { isOpen: isRefundOpen, onOpen: onRefundOpen, onClose: onRefundClose } = useDisclosure()
  const [selectedTicket, setSelectedTicket] = useState<TicketData | null>(null)

  // Data
  const { data: bookingsData, isLoading } = useQuery(
    'tickets',
    () => cmsService.getBookings({ limit: 100 }),
    { retry: false }
  )

  const allTickets: TicketData[] = bookingsData?.data || []

  // Derive unique routes for route filter
  const uniqueRoutes = Array.from(
    new Map(allTickets.filter(t => t.route).map(t => [t.route!.id, t.route!])).values()
  )

  // Stats
  const stats = [
    { label: 'Total Tickets', value: allTickets.length, color: 'blue', icon: Ticket },
    { label: 'Confirmed', value: allTickets.filter(t => t.status === 'confirmed').length, color: 'green', icon: CheckCircle },
    { label: 'Unused (Not Boarded)', value: allTickets.filter(isUnused).length, color: 'orange', icon: AlertTriangle, filter: () => setFilterUsage('unused') },
    { label: 'Refundable', value: allTickets.filter(canRefund).length, color: 'yellow', icon: RefreshCcw, filter: () => { setFilterUsage('unused'); setFilterPayment('paid') } },
    { label: 'Refunded', value: allTickets.filter(t => t.payment?.status === 'refunded').length, color: 'purple', icon: RotateCcw },
    { label: 'Cancelled', value: allTickets.filter(t => t.status === 'cancelled').length, color: 'red', icon: XCircle },
    { label: 'Total Revenue', value: allTickets.filter(t => t.payment?.status === 'paid').reduce((s, t) => s + (t.totalFare ?? 0), 0), color: 'teal', icon: CreditCard, isMoney: true },
  ]

  // Filtering
  const filtered = allTickets.filter(t => {
    const q = search.toLowerCase()
    const matchSearch =
      t.bookingReference?.toLowerCase().includes(q) ||
      t.passenger?.name?.toLowerCase().includes(q) ||
      t.passenger?.phone?.toLowerCase().includes(q) ||
      t.route?.name?.toLowerCase().includes(q)

    const matchStatus = filterStatus === 'all' || t.status === filterStatus
    const matchPayment = filterPayment === 'all' || (t.payment?.status ?? 'pending') === filterPayment
    const matchUsage =
      filterUsage === 'all' ||
      (filterUsage === 'unused' && isUnused(t)) ||
      (filterUsage === 'boarded' && !!t.boardedAt)
    const matchRoute = filterRoute === 'all' || t.route?.id === filterRoute

    return matchSearch && matchStatus && matchPayment && matchUsage && matchRoute
  })

  const totalPages = Math.ceil(filtered.length / PER_PAGE)
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleOpenDetail = (t: TicketData) => {
    setSelectedTicket(t)
    onDetailOpen()
  }

  const handleOpenRefund = (t: TicketData) => {
    setSelectedTicket(t)
    onRefundOpen()
  }

  const resetFilters = () => {
    setSearch('')
    setFilterStatus('all')
    setFilterPayment('all')
    setFilterUsage('all')
    setFilterRoute('all')
    setPage(1)
  }

  const activeFilterCount = [
    filterStatus !== 'all',
    filterPayment !== 'all',
    filterUsage !== 'all',
    filterRoute !== 'all',
  ].filter(Boolean).length

  if (isLoading) {
    return (
      <Center h="64">
        <Spinner size="lg" color="brand.500" />
      </Center>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={1}>
          <Heading size="xl" color="gray.900">Ticket Management</Heading>
          <Text color="gray.600">
            View, monitor, and refund unused tickets · {filtered.length} ticket{filtered.length !== 1 ? 's' : ''}
            {activeFilterCount > 0 && <Text as="span" color="orange.500" fontWeight="semibold"> ({activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active)</Text>}
          </Text>
        </VStack>
        <HStack spacing={3}>
          <Button leftIcon={<Download size={16} />} variant="outline" size="sm" borderRadius="xl">Export</Button>
          {filterUsage !== 'unused' && (
            <Button
              leftIcon={<AlertTriangle size={16} />}
              colorScheme="orange"
              variant="outline"
              size="sm"
              borderRadius="xl"
              onClick={() => { setFilterUsage('unused'); setPage(1) }}
            >
              Show Unused ({allTickets.filter(isUnused).length})
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Stats */}
      <SimpleGrid columns={{ base: 2, md: 6 }} spacing={4}>
        {stats.map((s, i) => (
          <Card
            key={i}
            bg="white"
            shadow="sm"
            cursor={s.filter ? 'pointer' : 'default'}
            onClick={s.filter}
            _hover={s.filter ? { shadow: 'md', transform: 'translateY(-1px)' } : {}}
            transition="all 0.2s"
          >
            <CardBody>
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" lineHeight="1.4">{s.label}</Text>
                  <Text fontSize={s.isMoney ? 'md' : 'xl'} fontWeight="bold" color="gray.900">
                    {s.isMoney ? fmtAmt(s.value) : s.value}
                  </Text>
                </VStack>
                <Box p={2} bg={`${s.color}.100`} borderRadius="lg" color={`${s.color}.600`}>
                  <s.icon size={20} />
                </Box>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* Active filters chips */}
      {activeFilterCount > 0 && (
        <Wrap spacing={2}>
          {filterUsage !== 'all' && (
            <WrapItem>
              <Tag colorScheme="orange" borderRadius="full" size="sm">
                <TagLeftIcon as={AlertTriangle} />
                <TagLabel>{filterUsage === 'unused' ? 'Unused Only' : 'Boarded Only'}</TagLabel>
              </Tag>
            </WrapItem>
          )}
          {filterStatus !== 'all' && (
            <WrapItem>
              <Tag colorScheme="blue" borderRadius="full" size="sm">
                <TagLabel>Status: {filterStatus}</TagLabel>
              </Tag>
            </WrapItem>
          )}
          {filterPayment !== 'all' && (
            <WrapItem>
              <Tag colorScheme="green" borderRadius="full" size="sm">
                <TagLabel>Payment: {filterPayment}</TagLabel>
              </Tag>
            </WrapItem>
          )}
          {filterRoute !== 'all' && (
            <WrapItem>
              <Tag colorScheme="purple" borderRadius="full" size="sm">
                <TagLabel>Route: {uniqueRoutes.find(r => r.id === filterRoute)?.name}</TagLabel>
              </Tag>
            </WrapItem>
          )}
          <WrapItem>
            <Button size="xs" variant="ghost" colorScheme="gray" onClick={resetFilters} borderRadius="full">
              Clear all
            </Button>
          </WrapItem>
        </Wrap>
      )}

      {/* Filters */}
      <Card bg="white" shadow="sm">
        <CardBody>
          <VStack spacing={3} align="stretch">
            <HStack spacing={3} flexDirection={{ base: 'column', md: 'row' }} flexWrap="wrap">
              <InputGroup maxW={{ base: 'full', md: '380px' }}>
                <InputLeftElement pointerEvents="none">
                  <Search size={16} color="var(--chakra-colors-gray-400)" />
                </InputLeftElement>
                <Input
                  placeholder="Search by ref, name, phone, or route..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1) }}
                  borderRadius="xl"
                />
              </InputGroup>

              <Select
                maxW="160px"
                value={filterUsage}
                onChange={e => { setFilterUsage(e.target.value); setPage(1) }}
                borderRadius="xl"
              >
                <option value="all">All Usage</option>
                <option value="unused">🎫 Unused (Not Boarded)</option>
                <option value="boarded">✅ Boarded</option>
              </Select>

              <Select
                maxW="160px"
                value={filterStatus}
                onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
                borderRadius="xl"
              >
                <option value="all">All Status</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
                <option value="no_show">No Show</option>
              </Select>

              <Select
                maxW="160px"
                value={filterPayment}
                onChange={e => { setFilterPayment(e.target.value); setPage(1) }}
                borderRadius="xl"
              >
                <option value="all">All Payments</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </Select>

              {uniqueRoutes.length > 0 && (
                <Select
                  maxW="200px"
                  value={filterRoute}
                  onChange={e => { setFilterRoute(e.target.value); setPage(1) }}
                  borderRadius="xl"
                >
                  <option value="all">All Routes</option>
                  {uniqueRoutes.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </Select>
              )}

              {activeFilterCount > 0 && (
                <Button size="sm" variant="ghost" colorScheme="gray" onClick={resetFilters} borderRadius="xl">
                  Reset
                </Button>
              )}
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Table */}
      <Card bg="white" shadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md" color="gray.900">Tickets</Heading>
            <HStack spacing={2}>
              {filterUsage === 'unused' && (
                <Badge colorScheme="orange" borderRadius="full" px={3} py={1}>
                  {filtered.length} unused ticket{filtered.length !== 1 ? 's' : ''}
                </Badge>
              )}
              <Text color="gray.500" fontSize="sm">{filtered.length} total</Text>
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th py={3} borderColor="gray.200">Reference</Th>
                  <Th py={3} borderColor="gray.200">Commuter</Th>
                  <Th py={3} borderColor="gray.200">Route</Th>
                  <Th py={3} borderColor="gray.200">Departure</Th>
                  <Th py={3} borderColor="gray.200">Fare</Th>
                  <Th py={3} borderColor="gray.200">Boarded</Th>
                  <Th py={3} borderColor="gray.200">Status</Th>
                  <Th py={3} borderColor="gray.200">Payment</Th>
                  <Th py={3} borderColor="gray.200">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginated.length === 0 ? (
                  <Tr>
                    <Td colSpan={9} py={16}>
                      <Center>
                        <VStack spacing={3}>
                          <Box p={4} bg="gray.100" borderRadius="full">
                            <Ticket size={40} color="var(--chakra-colors-gray-400)" />
                          </Box>
                          <Text fontWeight="medium" color="gray.600">No tickets match your filters</Text>
                          <Button size="sm" variant="ghost" colorScheme="gray" onClick={resetFilters}>Reset filters</Button>
                        </VStack>
                      </Center>
                    </Td>
                  </Tr>
                ) : (
                  paginated.map(ticket => {
                    const unused = isUnused(ticket)
                    return (
                      <Tr
                        key={ticket.id}
                        _hover={{ bg: unused ? 'orange.50' : 'gray.50' }}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                        bg={unused ? 'orange.25' : 'transparent'}
                      >
                        <Td py={3}>
                          <VStack align="start" spacing={0}>
                            <Text
                              fontWeight="semibold"
                              color="blue.600"
                              cursor="pointer"
                              fontSize="sm"
                              _hover={{ textDecoration: 'underline' }}
                              onClick={() => handleOpenDetail(ticket)}
                            >
                              {ticket.bookingReference}
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              {new Date(ticket.createdAt).toLocaleDateString()}
                            </Text>
                          </VStack>
                        </Td>
                        <Td py={3}>
                          <VStack align="start" spacing={0}>
                            <HStack spacing={1}>
                              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                {ticket.passenger?.name ?? '—'}
                              </Text>
                              {ticket.passenger?.isGuest && (
                                <Badge colorScheme="purple" fontSize="0.55em" borderRadius="full">Guest</Badge>
                              )}
                            </HStack>
                            <Text fontSize="xs" color="gray.500">{ticket.passenger?.phone}</Text>
                          </VStack>
                        </Td>
                        <Td py={3}>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" color="gray.900">{ticket.route?.name ?? '—'}</Text>
                            <Text fontSize="xs" color="gray.400">
                              {ticket.route?.startPoint} → {ticket.route?.endPoint}
                            </Text>
                          </VStack>
                        </Td>
                        <Td py={3}>
                          <Text fontSize="xs" color="gray.700">
                            {ticket.trip?.scheduledDeparture
                              ? new Date(ticket.trip.scheduledDeparture).toLocaleString(undefined, {
                                  dateStyle: 'short',
                                  timeStyle: 'short',
                                })
                              : '—'}
                          </Text>
                        </Td>
                        <Td py={3}>
                          <Text fontSize="sm" fontWeight="semibold" color="green.700">
                            {fmtAmt(ticket.totalFare)}
                          </Text>
                        </Td>
                        <Td py={3}>
                          {ticket.boardedAt ? (
                            <Badge colorScheme="green" borderRadius="full" fontSize="xs" px={2}>✓ Yes</Badge>
                          ) : unused ? (
                            <Badge colorScheme="orange" borderRadius="full" fontSize="xs" px={2}>🎫 No</Badge>
                          ) : (
                            <Badge colorScheme="gray" borderRadius="full" fontSize="xs" px={2}>—</Badge>
                          )}
                        </Td>
                        <Td py={3}>
                          <Badge
                            colorScheme={bookingStatusColor(ticket.status)}
                            borderRadius="full"
                            fontSize="xs"
                            px={2}
                            textTransform="capitalize"
                          >
                            {ticket.status}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge
                            colorScheme={paymentStatusColor(ticket.payment?.status)}
                            borderRadius="full"
                            fontSize="xs"
                            px={2}
                            textTransform="capitalize"
                          >
                            {ticket.payment?.status ?? 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <HStack spacing={1}>
                            <Tooltip label="Open Ticket">
                              <IconButton
                                aria-label="View"
                                icon={<Eye size={15} />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => handleOpenDetail(ticket)}
                              />
                            </Tooltip>
                            {canRefund(ticket) && (
                              <Tooltip label="Void & Refund to Wallet">
                                <IconButton
                                  aria-label="Refund"
                                  icon={<RefreshCcw size={15} />}
                                  size="sm"
                                  variant="solid"
                                  colorScheme="orange"
                                  onClick={() => handleOpenRefund(ticket)}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    )
                  })
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>

        {/* Pagination */}
        {totalPages > 1 && (
          <HStack justify="center" py={4} borderTop="1px solid" borderColor="gray.100" spacing={2}>
            <Button size="sm" variant="outline" borderRadius="lg" onClick={() => setPage(1)} isDisabled={page === 1}>«</Button>
            <Button size="sm" variant="outline" borderRadius="lg" onClick={() => setPage(p => Math.max(p - 1, 1))} isDisabled={page === 1}>Previous</Button>
            <Text fontSize="sm" color="gray.600" px={3}>Page {page} of {totalPages}</Text>
            <Button size="sm" variant="outline" borderRadius="lg" onClick={() => setPage(p => Math.min(p + 1, totalPages))} isDisabled={page === totalPages}>Next</Button>
            <Button size="sm" variant="outline" borderRadius="lg" onClick={() => setPage(totalPages)} isDisabled={page === totalPages}>»</Button>
          </HStack>
        )}
      </Card>

      {/* Modals */}
      <TicketDetailModal
        isOpen={isDetailOpen}
        onClose={onDetailClose}
        ticket={selectedTicket}
        onRefund={handleOpenRefund}
      />
      <RefundModal
        isOpen={isRefundOpen}
        onClose={onRefundClose}
        ticket={selectedTicket}
      />
    </VStack>
  )
}
